import type { LocationQueryRaw, RouteLocationRaw } from 'vue-router'
import { sanitizeSearchQuery } from '#shared/utils/search'
import { isNavigationFailure, useRouter } from 'vue-router'
import { SEARCH_DRAFT_SETTLE_MS } from '~/constants/search'

export function useSearchRouteCommit() {
  const router = useRouter()
  const draft = useState('search-route-draft', () => false)
  const writing = useState('search-route-writing', () => false)

  let queue: Promise<void> = Promise.resolve()
  let settleTimer: ReturnType<typeof setTimeout> | undefined
  let draftHasCommit = false

  if (import.meta.client) {
    onScopeDispose(router.afterEach((_to, _from, failure) => {
      if (!failure && !writing.value) {
        draft.value = false
      }
    }))
    onScopeDispose(() => {
      if (settleTimer !== undefined) {
        cancelSettle()
        draft.value = false
      }
    })
  }

  function cancelSettle() {
    clearTimeout(settleTimer)
    settleTimer = undefined
  }

  function startSettle() {
    cancelSettle()
    settleTimer = setTimeout(() => {
      // Only ends the draft. The next keystroke pushes the new entry, since browsers may skip timer-pushed entries.
      settleTimer = undefined
      draft.value = false
    }, SEARCH_DRAFT_SETTLE_MS)
  }

  function enqueue(task: () => Promise<void>) {
    const run = queue.then(task)
    queue = run.catch(() => {})
    return run
  }

  async function navigate(location: RouteLocationRaw, mode: 'push' | 'replace') {
    writing.value = true
    try {
      return !isNavigationFailure(await router[mode](location))
    }
    catch {
      return false
    }
    finally {
      writing.value = false
    }
  }

  function currentSq() {
    const sq = router.currentRoute.value.query.sq
    return typeof sq === 'string' ? sq : ''
  }

  function searchLocation(term: string, resetPage: boolean): RouteLocationRaw {
    const query: LocationQueryRaw = { ...router.currentRoute.value.query, sq: term || undefined }
    if (resetPage) {
      query.page = undefined
    }
    return { name: 'search-index', query }
  }

  async function pushDraft() {
    const { path, query, hash } = router.currentRoute.value
    if (await navigate({ path, query, hash, force: true }, 'push')) {
      draft.value = true
      draftHasCommit = false
    }
  }

  // The draft entry is pushed from the keystroke itself: browsers may skip history entries pushed later from a timer.
  function beginDraft() {
    cancelSettle()
    return enqueue(async () => {
      if (!draft.value && !writing.value && router.currentRoute.value.name === 'search-index') {
        await pushDraft()
      }
      if (draft.value && draftHasCommit) {
        startSettle()
      }
    })
  }

  function commitTerm(value: string) {
    return enqueue(async () => {
      const term = sanitizeSearchQuery(value)
      const previous = currentSq()
      if (term === previous) {
        return
      }
      if (term === sanitizeSearchQuery(previous)) {
        await navigate(searchLocation(term, false), 'replace')
        return
      }
      if (await navigate(searchLocation(term, true), draft.value ? 'replace' : 'push')) {
        draft.value = true
        draftHasCommit = true
        startSettle()
      }
    })
  }

  function submit(value: string) {
    return enqueue(async () => {
      const term = sanitizeSearchQuery(value)
      const previous = currentSq()
      const isNewSearch = term !== sanitizeSearchQuery(previous)
      if (term !== previous || router.currentRoute.value.query.page !== undefined) {
        await navigate(searchLocation(term, true), isNewSearch && !draft.value ? 'push' : 'replace')
      }
      draft.value = false
    })
  }

  function clear() {
    return enqueue(async () => {
      cancelSettle()
      if (!draft.value) {
        await pushDraft()
        if (!draft.value) {
          return
        }
      }
      if (await navigate(searchLocation('', true), 'replace')) {
        draftHasCommit = false
      }
    })
  }

  return {
    beginDraft,
    submit,
    clear,
    commitTerm,
  }
}
